export type PropertyContextOption = {
  id: string;
  label: string;
  isDefault?: boolean;
};

const ACTIVE_PROPERTY_STORAGE_KEY = "xentros.activePropertyId";
const ACTIVE_PROPERTY_EVENT = "xentros-active-property-change";

type ActivePropertyChangeEvent = CustomEvent<{ propertyId: string | null }>;

function hasWindow() {
  return typeof window !== "undefined";
}

function normalizeProperties(properties: PropertyContextOption[]) {
  return properties.filter((property) => property.id.trim());
}

function getFallbackPropertyId(properties: PropertyContextOption[]) {
  const normalized = normalizeProperties(properties);
  return normalized.find((property) => property.isDefault)?.id ?? normalized[0]?.id ?? null;
}

function isAllowedProperty(propertyId: string | null | undefined, properties: PropertyContextOption[]) {
  if (!propertyId) return false;
  return normalizeProperties(properties).some((property) => property.id === propertyId);
}

export function getActivePropertyId(properties: PropertyContextOption[]) {
  const fallbackId = getFallbackPropertyId(properties);
  if (!hasWindow()) return fallbackId;

  const storedId = window.localStorage.getItem(ACTIVE_PROPERTY_STORAGE_KEY);
  return isAllowedProperty(storedId, properties) ? storedId : fallbackId;
}

export function setActivePropertyId(propertyId: string, properties: PropertyContextOption[]) {
  const nextPropertyId = isAllowedProperty(propertyId, properties) ? propertyId : getFallbackPropertyId(properties);
  if (!hasWindow() || !nextPropertyId) return nextPropertyId;

  window.localStorage.setItem(ACTIVE_PROPERTY_STORAGE_KEY, nextPropertyId);
  window.dispatchEvent(
    new CustomEvent(ACTIVE_PROPERTY_EVENT, {
      detail: { propertyId: nextPropertyId },
    })
  );
  return nextPropertyId;
}

export function subscribeToActiveProperty(listener: (propertyId: string | null) => void) {
  if (!hasWindow()) return () => {};

  const onPropertyChange = (event: Event) => {
    listener((event as ActivePropertyChangeEvent).detail.propertyId);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === ACTIVE_PROPERTY_STORAGE_KEY) listener(event.newValue);
  };

  window.addEventListener(ACTIVE_PROPERTY_EVENT, onPropertyChange);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(ACTIVE_PROPERTY_EVENT, onPropertyChange);
    window.removeEventListener("storage", onStorage);
  };
}

